import React, { useState } from 'react';
import '../healthoverview.css';
import { Card, CardContent, Typography } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area } from 'recharts';
import ChartControls from './ChartControls';
import CustomTooltip from './CustomTooltip';
import { useQuery } from 'react-query';
import {
  getFallDetectionGraphData,
  getRecipientFallDetectionGraphData,
  getGaitSpeedGraphData,
  getRecipientGaitSpeedGraphData,
} from '../../../utils/api';
import { fixedDecimalToTwo, getFormattedChartPeriodLabel } from '../../../utils';

export default function MobilityGraph({ userData, recipientId, type }) {
  const userId = userData?._id || userData?.id;
  const [period, setPeriod] = useState('monthly');
  const [showComparison, setShowComparison] = useState(false);
  let queryKey = ['fallDetectionGraph', userId, recipientId, period, showComparison];
  let queryFn = () =>
    recipientId
      ? getRecipientFallDetectionGraphData({ userId, recipientId, period, showComparison })
      : getFallDetectionGraphData({ userId, period, showComparison });

  if (type === 'Gait Speed') {
    queryKey = ['gaitSpeedGraph', userId, recipientId, period, showComparison];
    queryFn = () =>
      recipientId
        ? getRecipientGaitSpeedGraphData({ userId, recipientId, period, showComparison })
        : getGaitSpeedGraphData({ userId, type, period, showComparison });
  }
  const { data: apiResponse } = useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    staleTime: 60000,
    retry: 2,
  });
  const getChartData = () => {
    if (!apiResponse?.data || apiResponse.data.length === 0) {
      return [{ period: 'No data available', current: 0, previous: 0 }];
    }
    return apiResponse.data.map((item) => {
      return {
        period: getFormattedChartPeriodLabel(period, item.period),
        current: fixedDecimalToTwo(item.data),
        previous: fixedDecimalToTwo(item.prevData),
      };
    });
  };

  const chartData = getChartData();

  const getCurrentValues = () => {
    return chartData.map((item) => item.current).filter((val) => val > 0);
  };

  const chartValues = getCurrentValues();
  const avgGaitSpeed = chartValues.length
    ? (chartValues.reduce((sum, val) => sum + parseFloat(val), 0) / chartValues.length).toFixed(2)
    : 0;
  const highestGaitSpeed = chartValues.length ? Math.max(...chartValues) : 0;
  const lowestGaitSpeed = chartValues.length ? Math.min(...chartValues) : 0;

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const toggleComparison = () => {
    setShowComparison(!showComparison);
  };

  return (
    <div className="heart-rate-container">
      <div className="header-section" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '30px' }}>
        <ChartControls
          period={period}
          handlePeriodChange={handlePeriodChange}
          showComparison={showComparison}
          toggleComparison={toggleComparison}
          dataType={type}
          userId={userId}
          selectedRecipient={recipientId}
        />
      </div>

      <Card className="stats-card">
        <CardContent className="stats-content">
          <div className="stat-item">
            <Typography color="textSecondary" className="stat-label" variant="caption">
              Avg
            </Typography>
            <Typography variant="h6" component="p" className="stat-value">
              {avgGaitSpeed}
            </Typography>
          </div>
          <div className="stat-item">
            <Typography color="textSecondary" className="stat-label" variant="caption">
              Highest
            </Typography>
            <Typography variant="subtitle1" component="p" className="stat-value">
              {highestGaitSpeed}
            </Typography>
          </div>
          <div className="stat-item">
            <Typography color="textSecondary" className="stat-label" variant="caption">
              Lowest
            </Typography>
            <Typography variant="subtitle1" component="p" className="stat-value">
              {lowestGaitSpeed}
            </Typography>
          </div>
        </CardContent>
      </Card>
      <div className="chart-container" style={{ height: '400px', width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gaitGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="rgba(59, 155, 143, 0.2)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="rgba(59, 155, 143, 0.1)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gaitGradientPrevious" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="rgba(144, 238, 144, 0.2)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="rgba(144, 238, 144, 0.1)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fill: '#888' }} />
            <YAxis
              domain={[0, 4]}
              ticks={[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4]}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#888' }}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />

            {showComparison && (
              <Area
                type="monotone"
                dataKey="previous"
                stroke="#90EE90"
                fillOpacity={1}
                fill="url(#gaitGradientPrevious)"
                name="previous"
              />
            )}

            <Area
              type="monotone"
              dataKey="current"
              stroke="#3b9b8f"
              fillOpacity={1}
              fill="url(#gaitGradient)"
              name="current"
            />

            {showComparison && (
              <Line
                type="monotone"
                dataKey="previous"
                stroke="#90EE90"
                strokeWidth={3}
                dot={{ r: 4, fill: '#90EE90', strokeWidth: 0 }}
                activeDot={{ r: 6, fill: '#90EE90', stroke: '#fff', strokeWidth: 2 }}
                isAnimationActive={true}
                name="previous"
              />
            )}

            <Line
              type="monotone"
              dataKey="current"
              stroke="#3b9b8f"
              strokeWidth={3}
              dot={{ r: 4, fill: '#3b9b8f', strokeWidth: 0 }}
              activeDot={{ r: 6, fill: '#3b9b8f', stroke: '#fff', strokeWidth: 2 }}
              isAnimationActive={true}
              name="current"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
